import { SupportedLanguage } from '@foundation/localization';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { useEffect, useState, JSX } from 'react';

import { LoadingSpinner, LoadingSpinnerSize } from '@/components/common';
import { AlertWarning } from '@/components/common/alerts';
import { PayButton } from '@/components/pages/stored-instruments/utils/PayButton';
import Logger, { LoggerOrigin } from '@/services/Logger';
import TelemetryService from '@/services/telemetry/TelemetryService';
import WorldpayKlarnaService from '@/services/worldpay/WorldpayKlarnaService';
import { redirect } from '@/utils/redirect';

interface IPayWithWorldpayKlarnaBtnProps {
  navigation?: JSX.Element | null;
}

export function PayWithWorldpayKlarnaBtn({
  navigation,
}: IPayWithWorldpayKlarnaBtnProps) {
  const { accessToken } = useAuth();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    setLoading(true);
    setError(undefined);
    WorldpayKlarnaService.getRedirectUrl(
      language as SupportedLanguage,
      accessToken ?? '',
    )
      .then(setRedirectUrl)
      .catch((e: Error) => {
        TelemetryService.trackException(e, 'WorldpayKlarnaBtn');
        Logger.error(
          LoggerOrigin.ReactComponent,
          'Klarna URL fetch failure',
          e,
        );
        setError(e);
      })
      .finally(() => setLoading(false));
  }, [accessToken, language]);

  if (error) {
    return (
      <AlertWarning
        title={t('Something went wrong!')}
        description={t('Klarna payments are currently unavailable.')}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="h-14">
        <LoadingSpinner size={LoadingSpinnerSize.Small} />
      </div>
    );
  }

  return (
    <div className="flex space-x-2">
      {navigation}
      <PayButton onClick={() => redirect(redirectUrl)} />
    </div>
  );
}
